
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Wallet, 
  ListChecks, 
  CreditCard 
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useIncomeSummary } from '@/hooks/useIncomeSummary';
import IncomeChart from '@/components/statistics/IncomeChart';

const IncomeSummaryPage = () => {
  // Call all hooks at the beginning of the component
  const { incomeData, treatmentData, isLoading } = useIncomeSummary();
  const [dateFilter, setDateFilter] = useState('all');
  const [patientFilter, setPatientFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter treatment data based on selected filters
  const filteredTreatments = treatmentData.filter(treatment => {
    const matchesDate = dateFilter === 'all' || 
      (dateFilter === 'thisMonth' && new Date(treatment.date).getMonth() === new Date().getMonth()) ||
      (dateFilter === 'lastMonth' && new Date(treatment.date).getMonth() === new Date().getMonth() - 1);
    
    const matchesPatient = patientFilter === 'all' || treatment.patientName.includes(patientFilter);
    const matchesService = serviceFilter === 'all' || treatment.service === serviceFilter;
    const matchesStatus = statusFilter === 'all' || treatment.status === statusFilter;
    
    return matchesDate && matchesPatient && matchesService && matchesStatus;
  });

  // Get unique service types for filter
  const serviceTypes = [...new Set(treatmentData.map(t => t.service))];
  
  // Get unique patient names for filter
  const patientNames = [...new Set(treatmentData.map(t => t.patientName))];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">My Income Summary</h1>
      </div>

      {/* Income Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Income</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₺{incomeData.totalIncome.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">All time total from services</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Services Completed</CardTitle>
            <ListChecks className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{incomeData.totalServices}</div>
            <p className="text-xs text-muted-foreground">Across all patients</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Average Case Value</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₺{incomeData.averageCaseValue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Per completed service</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Income Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Income (Last 12 Months)</CardTitle>
        </CardHeader>
        <CardContent>
          <IncomeChart data={incomeData} />
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <Label htmlFor="dateFilter">Date</Label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger id="dateFilter">
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="thisMonth">This month</SelectItem>
                  <SelectItem value="lastMonth">Last month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="patientFilter">Patient</Label>
              <Select value={patientFilter} onValueChange={setPatientFilter}>
                <SelectTrigger id="patientFilter">
                  <SelectValue placeholder="Select patient" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All patients</SelectItem>
                  {patientNames.map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="serviceFilter">Service Type</Label>
              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger id="serviceFilter">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All services</SelectItem>
                  {serviceTypes.map(service => (
                    <SelectItem key={service} value={service}>{service}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="statusFilter">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger id="statusFilter">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Treatments Table */}
      <Card>
        <CardHeader>
          <CardTitle>Treatment Income Details</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient Name</TableHead>
                  <TableHead>Service Provided</TableHead>
                  <TableHead>Date of Treatment</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTreatments.map((treatment) => (
                  <TableRow key={treatment.id}>
                    <TableCell>{treatment.patientName}</TableCell>
                    <TableCell>{treatment.service}</TableCell>
                    <TableCell>{new Date(treatment.date).toLocaleDateString()}</TableCell>
                    <TableCell>₺{treatment.amount.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={treatment.status === 'completed' ? 'default' : 'destructive'}
                      >
                        {treatment.status.charAt(0).toUpperCase() + treatment.status.slice(1)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredTreatments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No treatments found matching the selected filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default IncomeSummaryPage;
